import type { CaptionQuestionAnswer, PostMedia } from '../../types/publishing.js';
import { DESTINATION_CAPTION_LIMIT } from './characterLimits.js';
import { destinationById, type PostDestinationId } from './destinations.js';

// Publishing — AI caption drafting: WHEN it is offered, and WHAT LENGTH it must
// write to. Pure and SHARED (the composer decides whether to render the control;
// the server decides what to put in the prompt), so the two can never disagree
// about whether a composition is draftable.
//
// ⚠️ STORIES TAKE NO CAPTION (characterLimits.ts: their limit is `null`, not a
// number — Facebook Page Stories and Instagram Stories publish no message field).
// So a story-ONLY composition has nothing to draft, and the control says why
// rather than generating copy that will never be published anywhere.

/**
 * What the composer sends to POST /api/publishing/draft-caption. Lives here
 * rather than in src/types/publishing.ts because it carries PostDestinationId,
 * whose module already imports from that file.
 */
export interface CaptionDraftRequest {
  /**
   * The account whose voice grounds the drafts. On the multi-account composer
   * this is the FIRST selected account (mirroring how its live preview resolves),
   * with `sharedAcrossAccounts` telling the model the caption is going to more
   * than one business.
   */
  kyckAccountId: string;
  destinations: PostDestinationId[];
  /** The AM's "what's this post about?" steer. May be empty. */
  brief: string;
  /**
   * How it should sound. Free text, passed to the model verbatim as a STYLING
   * instruction. ⚠️ It can never license breaking a hard rule; see rule G in
   * captionDraftPrompt.ts.
   */
  tone: string;
  /** None / Light / Generous. See CaptionEmojiLevel. */
  emoji: CaptionEmojiLevel;
  /** 0-3 pages for the model to draw facts from. */
  referenceUrls: string[];
  /** The composer's current media — the first asset may be sent as vision input. */
  media: PostMedia[];
  /**
   * The AM's answers to the clarifying questions. Empty when they pressed
   * "Just write it" — drafting behaves identically either way.
   */
  answers: CaptionQuestionAnswer[];
  sharedAcrossAccounts: boolean;
}

/**
 * A follow-up revision of one caption.
 *
 * ⚠️⚠️ DELIBERATELY STATELESS. It carries the CURRENT text plus one instruction —
 * not an accumulating conversation. Two reasons, and both are the point rather
 * than a shortcut: cost stays flat no matter how many times the AM iterates
 * (a ten-turn thread would re-send every previous turn), and the context cannot
 * drift — turn six is grounded in the same brief, tone and reference material as
 * turn one, instead of in five rounds of its own output. A full multi-turn
 * conversation was considered and deferred; if it is ever built, the reason to
 * do it would be genuine back-and-forth ("why did you drop the price?"), which
 * is a different feature from iterating on a caption.
 */
export interface CaptionRefineRequest extends CaptionDraftRequest {
  /** The text being revised — the LATEST caption, including any manual edits. */
  currentDraft: string;
  /** "punchier", "add bullet points", "lead with the price". */
  instruction: string;
}

/**
 * ⚠️⚠️ THE THREE DRAFTS ARE THREE DELIBERATE STYLES, NOT THREE ATTEMPTS (round 3,
 * 2026-08-07). This is the structural change that makes the choice meaningful:
 * round 2 returned three variations on one idea, so the AM was picking between
 * three similar mediocrities rather than between genuinely different approaches
 * to the same post. Each style is written FULLY, at length — the model is told
 * explicitly not to budget effort across the three.
 *
 * The ids are the contract with the model (structured output) AND the labels the
 * workspace renders, so the choice is legible on screen rather than the AM having
 * to work out why three drafts differ. Shared so the prompt, the parser and the
 * UI cannot drift apart about what a style IS.
 */
export const CAPTION_DRAFT_STYLES = [
  {
    id: 'spec-led',
    label: 'Spec-led',
    blurb: 'Concrete facts up front — numbers, specs, the verdict.',
  },
  {
    id: 'story-led',
    label: 'Story-led',
    blurb: 'Opens on a scene the reader recognises, facts woven in.',
  },
  {
    id: 'punchy-list',
    label: 'Punchy list',
    blurb: 'Short hook, scannable lines, tight call to action.',
  },
] as const;

export type CaptionDraftStyleId = (typeof CAPTION_DRAFT_STYLES)[number]['id'];

export function isCaptionDraftStyle(value: unknown): value is CaptionDraftStyleId {
  return CAPTION_DRAFT_STYLES.some((s) => s.id === value);
}

export function captionStyleLabel(style: string): string {
  return CAPTION_DRAFT_STYLES.find((s) => s.id === style)?.label ?? 'Draft';
}

/**
 * ⚠️⚠️ EMOJI (round 4, 2026-08-07). Live drafts came back with NO emoji at all,
 * because the prompt said to follow the account's own examples and — with
 * grounding 'none' — there were none, so it defaulted to zero. That is wrong
 * twice over for social: emoji are platform convention, and the agency's own
 * contact blocks are full of 📞 📍 👉.
 *
 * ⚠️ 'light' IS THE DEFAULT AND MEANS "THE EXAMPLES LEAD". An account that never
 * uses emoji must not start getting them just because the control sits at its
 * default; but with NO examples the fallback is a tasteful few, not silence —
 * absence of examples is not evidence the account dislikes emoji. 'none' and
 * 'generous' are explicit human overrides and win outright.
 *
 * Per-brand emoji norms belong in the VOICE PROFILE (next build); this control
 * stays as the per-post override on top of it.
 */
export type CaptionEmojiLevel = 'none' | 'light' | 'generous';

export const EMOJI_LEVELS: Array<{ id: CaptionEmojiLevel; label: string; hint: string }> = [
  { id: 'none', label: 'None', hint: 'No emoji at all' },
  { id: 'light', label: 'Light', hint: "A few, following the account's own posts" },
  { id: 'generous', label: 'Generous', hint: 'Lean into them — bullets, signposts, personality' },
];

/**
 * One-tap starting points for the tone field.
 *
 * ⚠️⚠️ A CHIP FILLS THE FIELD WITH THE FULL DESCRIPTIVE PHRASE, NOT THE LABEL
 * (round 4). "Premium" alone is a word the model has to guess at; "Premium and
 * understated — restrained, confident, quality over hype, minimal exclamation"
 * is an instruction. It also shows the AM what the option actually MEANS before
 * they use it. The field stays FREE TEXT — a chip sets a starting point they
 * edit, never a closed enum.
 *
 * ⚠️ The seven replace the round-2 five, which were derived from a single
 * automotive example: "tradie-friendly" is one narrow AUDIENCE rather than a
 * tone, and a third of the book is fitness. These are industry-agnostic and
 * mutually distinct — each produces materially different copy, not a different
 * flavour of "nice".
 *
 * The voice profile (next build) will carry a per-brand DEFAULT tone, so an AM
 * starts from that marque's usual voice and these become the per-post override.
 */
export const TONE_SUGGESTIONS: Array<{ label: string; phrase: string }> = [
  {
    label: 'Straight-talking',
    phrase: 'Straight-talking — plain language, no fluff, get to the point',
  },
  {
    label: 'Warm & local',
    phrase:
      'Warm and local — friendly, community-minded, like a neighbour not a corporation',
  },
  {
    label: 'High-energy',
    phrase: 'High-energy — urgent and exciting, built to drive action on an offer',
  },
  {
    label: 'Premium',
    phrase:
      'Premium and understated — restrained, confident, quality over hype, minimal exclamation',
  },
  {
    label: 'Enthusiast',
    phrase:
      'For enthusiasts — spec-deep and credible, written for people who genuinely know the product',
  },
  {
    label: 'Helpful',
    phrase: 'Helpful and advisory — explain, guide, answer the question the reader actually has',
  },
  {
    label: 'Playful',
    phrase: 'Playful — light humour, a bit of personality, never at the expense of clarity',
  },
];

export const STORY_ONLY_MESSAGE =
  "Stories don't publish a caption, so there's nothing to draft. Tick Facebook, Instagram or Google Business to draft one.";

export type CaptionDraftAvailability =
  | {
      available: true;
      /**
       * The TIGHTEST limit among the ticked caption-bearing destinations, so one
       * caption works everywhere it is going. null when nothing is ticked yet —
       * the AM hasn't chosen a surface, and inventing a constraint would be worse
       * than stating none.
       */
      charLimit: number | null;
      /** Ticked destinations that actually publish a caption. */
      captionDestinations: PostDestinationId[];
    }
  | { available: false; reason: string };

export function captionDraftAvailability(
  selected: PostDestinationId[],
): CaptionDraftAvailability {
  const captionDestinations = selected.filter(
    (id) => DESTINATION_CAPTION_LIMIT[id] !== null,
  );
  if (selected.length > 0 && captionDestinations.length === 0) {
    return { available: false, reason: STORY_ONLY_MESSAGE };
  }
  const limits = captionDestinations
    .map((id) => DESTINATION_CAPTION_LIMIT[id])
    .filter((n): n is number => n !== null);
  return {
    available: true,
    charLimit: limits.length > 0 ? Math.min(...limits) : null,
    captionDestinations,
  };
}

/** "Facebook, Instagram and Google Business" — for the prompt and the panel. */
export function describeDestinations(ids: PostDestinationId[]): string {
  const labels = ids.map((id) => destinationById(id).label);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
